/**
 * Materials catalog snapshot / restore panel for Materials Database.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { History, Loader2, RotateCcw, Save } from 'lucide-react';
import {
  createMaterialsCatalogSnapshot,
  listMaterialsCatalogSnapshots,
  restoreMaterialsCatalogSnapshot,
  type MaterialsCatalogSnapshotMeta,
} from '../../utils/materialsCatalogSnapshotApi';

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MaterialsCatalogSnapshotsModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [snapshots, setSnapshots] = useState<MaterialsCatalogSnapshotMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [restoreId, setRestoreId] = useState<string | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listMaterialsCatalogSnapshots();
      setSnapshots(list);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to load snapshots.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void refresh();
  }, [isOpen, refresh]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setError('Label is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const snap = await createMaterialsCatalogSnapshot(trimmed, note.trim());
      setLabel('');
      setNote('');
      await refresh();
      alert(
        `Snapshot saved: ${snap.label}\n${snap.timberCount} timber + ${snap.hardwareCount} hardware rows.`,
      );
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to create snapshot.');
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreId) return;
    if (restoreConfirm.trim() !== 'RESTORE') {
      setError('Type RESTORE to confirm.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await restoreMaterialsCatalogSnapshot(restoreId, 'RESTORE');
      setRestoreId(null);
      setRestoreConfirm('');
      await refresh();
      alert(
        `Catalog restored.\nTimber: wrote ${result.timberWritten}, deleted ${result.timberDeleted}.\nHardware: wrote ${result.hardwareWritten}, deleted ${result.hardwareDeleted}.`,
      );
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Restore failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="import-modal-overlay" onClick={onClose}>
      <div
        className="import-modal materials-snapshot-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="materials-snapshot-title"
      >
        <div className="import-modal-header">
          <h2 id="materials-snapshot-title">
            <History size={20} /> Catalog Snapshots
          </h2>
          <button type="button" className="import-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="import-modal-body">
          <p className="materials-snapshot-blurb">
            Save a full timber + hardware catalog backup before major imports or price updates.
            Restore replaces the live catalogs only (not proposal line prices).
          </p>

          <div className="materials-snapshot-create">
            <div className="import-field">
              <label>Label</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. before-dorval-import"
                disabled={busy}
              />
            </div>
            <div className="import-field">
              <label>Note (optional)</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why this snapshot"
                disabled={busy}
              />
            </div>
            <button
              type="button"
              className="materials-action-btn primary"
              onClick={() => void handleCreate()}
              disabled={busy || !label.trim()}
            >
              {busy ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
              Save snapshot
            </button>
          </div>

          {error ? <div className="import-error">{error}</div> : null}

          <div className="materials-snapshot-list">
            <h3>History</h3>
            {loading ? (
              <p>Loading…</p>
            ) : snapshots.length === 0 ? (
              <p className="materials-snapshot-empty">No snapshots yet.</p>
            ) : (
              <ul>
                {snapshots.map((s) => (
                  <li key={s.id}>
                    <div className="materials-snapshot-meta">
                      <strong>{s.label}</strong>
                      <span>{formatWhen(s.createdAt)}</span>
                      <span>
                        {s.timberCount} timber · {s.hardwareCount} hardware
                        {s.byteSize ? ` · ${Math.round(s.byteSize / 1024)} KB` : ''}
                      </span>
                      {s.note ? <span className="note">{s.note}</span> : null}
                      {s.lastRestoredAt ? (
                        <span className="note">Last restored {formatWhen(s.lastRestoredAt)}</span>
                      ) : null}
                    </div>
                    <div className="materials-snapshot-actions">
                      <button
                        type="button"
                        className="materials-action-btn danger-btn"
                        disabled={busy}
                        onClick={() => {
                          setRestoreId(s.id);
                          setRestoreConfirm('');
                          setError('');
                        }}
                      >
                        <RotateCcw size={14} /> Restore
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {restoreId ? (
            <div className="materials-snapshot-restore-confirm">
              <p>
                Restore snapshot <code>{restoreId}</code>? This overwrites live timber and hardware
                catalogs. Type <strong>RESTORE</strong> to confirm.
              </p>
              <input
                type="text"
                value={restoreConfirm}
                onChange={(e) => setRestoreConfirm(e.target.value)}
                placeholder="RESTORE"
                disabled={busy}
                autoFocus
              />
              <div className="materials-snapshot-actions">
                <button
                  type="button"
                  className="materials-action-btn secondary"
                  disabled={busy}
                  onClick={() => {
                    setRestoreId(null);
                    setRestoreConfirm('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="materials-action-btn danger-btn"
                  disabled={busy || restoreConfirm.trim() !== 'RESTORE'}
                  onClick={() => void handleRestore()}
                >
                  Confirm restore
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MaterialsCatalogSnapshotsModal;
